import ConfirmButtonGroup from 'components/ui/forms/ConfirmButtonGroup';
import UploadSingleFileMemorized from 'components/ui/forms/files/UploadSingleFileMemorized';
import FormProvider from 'components/ui/forms/FormProvider';
import TextFieldMemorized from 'components/ui/forms/TextFieldMemorized';
import { useFormik } from 'formik';
import useSnackbarErrorHandler from 'hooks/snackbar/useSnackbarErrorHandler';
import useDI from 'hooks/useDI';
import useIsMountedRef from 'hooks/useIsMountedRef';
import useStableNavigate from 'hooks/useStableNavigate';
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import IFileService from 'services/file/IFileService';
import { RoutingServiceDashboard } from 'services/routing/RoutingService';
import { useSelector } from 'storage';
import { DocumentTemplateRequestDTO } from 'typings/dto/documentTemplate';
import { UploadFileResponseDTO } from 'typings/dto/files';
import * as Yup from 'yup';

type Props = {
  documentTemplate: DocumentTemplate | null;
};

function DocumentTemplateEditBlock({ documentTemplate }: Props) {
  const { services, storageActions, apiService } = useDI();
  const { translate } = services.language;
  const { currentUser } = useSelector((state) => state.auth);
  const pathParams = useParams();
  const handleFormErrors = useSnackbarErrorHandler();
  const navigate = useStableNavigate();
  const isEditMode = documentTemplate !== null;
  const isMountedRef = useIsMountedRef();
  const fieldIsRequiredText = translate('errors.fieldIsRequired');
  const isFileExist = documentTemplate !== null;

  useEffect(() => {
    if (documentTemplate?.id)
      try {
        apiService.subEntity.file.getActualFileData(documentTemplate.file).then((data) => {
          if (isMountedRef.current) {
            formState.setFieldValue('file', data);
          }
        });
      } catch (error) {
        handleFormErrors({ error });
      }
  }, []);

  const { initialValues, validationSchema } = useMemo(
    () => ({
      initialValues: {
        name: isEditMode ? documentTemplate.name : '',
        file: isEditMode ? documentTemplate.file : (null as IFileService.File_ | null),
      },
      validationSchema: Yup.object().shape({
        name: Yup.string().trim(fieldIsRequiredText).required(fieldIsRequiredText),
        file: Yup.mixed<File>().nonNullable().required(fieldIsRequiredText),
      }),
    }),
    [isEditMode, documentTemplate]
  );

  const formState = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        let uploadedFile: UploadFileResponseDTO | undefined;
        if (values.file?.id !== documentTemplate?.file.id || !isFileExist) {
          uploadedFile = await apiService.subEntity.file.uploadOneFile(values.file, values.name);
        }

        if (isEditMode) {
          const updateDto: DocumentTemplateRequestDTO & { id: string } = {
            id: documentTemplate.id,
            name: values.name,
            fileId: uploadedFile ? uploadedFile.id : documentTemplate.file.id,
          };
          await storageActions.models.documentTemplate.update(updateDto);
        } else {
          const createDto: DocumentTemplateRequestDTO = {
            name: values.name,
            fileId: uploadedFile!.id,
          };
          await storageActions.models.documentTemplate.create(createDto);
        }

        navigate(RoutingServiceDashboard.modelsDirectory.documentTemplate.getListPath(pathParams));
        await storageActions.models.documentTemplate.getList({
          tenantId: currentUser?.tenant.id || '',
        });
      } catch (error) {
        handleFormErrors({ callback: () => setSubmitting(false), error });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const cancelHandler = () => {
    navigate(RoutingServiceDashboard.modelsDirectory.documentTemplate.getListPath(pathParams));
  };
  return (
    <FormProvider formState={formState}>
      <TextFieldMemorized fieldName="name" label={translate('pages.documentTemplate.name')} formState={formState} required />
      <UploadSingleFileMemorized fieldName="file" formState={formState} />
      <ConfirmButtonGroup isLoading={formState.isSubmitting} cancelHandler={cancelHandler} />
    </FormProvider>
  );
}

export default React.memo(DocumentTemplateEditBlock);
